import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

export type TokenStatus = "idle" | "error" | "success";

@Component({
  selector: "app-token-input",
  templateUrl: "./token-input.component.html",
  styleUrls: ["./token-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TokenInputComponent),
      multi: true,
    },
  ],
})

export class TokenInputComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() length = 6;
  @Input() label = "Código de verificação (6 dígitos)";
  @Input() errorMessage =
    "Código inválido. Verifique os dígitos e tente novamente.";
  @Input() successMessage = "Código validado com sucesso!";
  @Input() resendSeconds = 60;

  //Controlado pelo componente pai conforme a resposta do Cognito
  @Input() set status(value: TokenStatus) {
    this._status = value || "idle";
    if (this._status === "error") {
      setTimeout(() => this.focusAt(0));
    }
  }
  get status(): TokenStatus {
    return this._status;
  }

  // Emite assim que os 6 dígitos estiverem preenchidos
  @Output() completed = new EventEmitter<string>();
  @Output() resend = new EventEmitter<void>();

  @ViewChildren("box") boxes: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  disabled = false;
  countdown = 0;

  private _status: TokenStatus = "idle";
  private intervalId: any = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.digits = new Array(this.length).fill("");
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  // ---------------- ControlValueAccessor ----------------

  writeValue(value: string): void {
    const clean = (value || "").replace(/\D/g, "").slice(0, this.length);
    this.digits = new Array(this.length).fill("");
    clean.split("").forEach((d, i) => (this.digits[i] = d));
    this.syncInputs();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ---------------- Eventos ----------------


  // Usa (input) em vez de (keyup) porque em teclados mobile o keyCode costuma vir como "Unidentified".
  onInput(event: any, index: number): void {
    const input = event.target as HTMLInputElement;
    const raw = (input.value || "").replace(/\D/g, "");

    if (this._status !== "idle") {
      this._status = "idle";
    }

    if (!raw) {
      this.digits[index] = "";
      input.value = "";
      this.emitValue();
      return;
    }

    if (raw.length > 1) {
      this.fillFrom(index, raw);
      return;
    }

    this.digits[index] = raw;
    input.value = raw;
    this.emitValue();

    if (index < this.length - 1) {
      this.focusAt(index + 1);
    }
    this.checkComplete();
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const key = event.key;

    if (key === "Backspace") {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = "";
        this.syncInputs();
        this.emitValue();
      } else if (index > 0) {
        this.digits[index - 1] = "";
        this.syncInputs();
        this.emitValue();
        this.focusAt(index - 1);
      }
      if (this._status !== "idle") {
        this._status = "idle";
      }
      return;
    }

    if (key === "Delete") {
      event.preventDefault();
      this.digits[index] = "";
      this.syncInputs();
      this.emitValue();
      return;
    }

    if (key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      this.focusAt(index - 1);
      return;
    }

    if (key === "ArrowRight" && index < this.length - 1) {
      event.preventDefault();
      this.focusAt(index + 1);
      return;
    }

    const isControl =
      event.ctrlKey ||
      event.metaKey ||
      key === "Tab" ||
      key === "Enter" ||
      key === "Home" ||
      key === "End";

    if (!isControl && key.length === 1 && !/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const clipboard =
      (event.clipboardData || (window as any).clipboardData).getData("text") ||
      "";
    const digits = clipboard.replace(/\D/g, "");
    if (!digits) {
      return;
    }
    const start = digits.length >= this.length ? 0 : index;
    this._status = "idle";
    this.fillFrom(start, digits);
  }

  onFocus(event: any): void {
    (event.target as HTMLInputElement).select();
  }

  onBlur(): void {
    this.onTouched();
  }

  onResend(): void {
    if (this.countdown > 0 || this.disabled) {
      return;
    }
    this.clear();
    this.resend.emit();
    this.startCountdown();
  }

  clear(): void {
    this.digits = new Array(this.length).fill("");
    this._status = "idle";
    this.syncInputs();
    this.emitValue();
    this.focusAt(0);
  }

  // ---------------- Helpers ----------------

  get value(): string {
    return this.digits.join("");
  }

  get countdownLabel(): string {
    const min = Math.floor(this.countdown / 60);
    const sec = this.countdown % 60;
    return `${this.pad(min)}:${this.pad(sec)}`;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private fillFrom(start: number, digits: string): void {
    for (let i = 0; i < digits.length && start + i < this.length; i++) {
      this.digits[start + i] = digits.charAt(i);
    }
    this.syncInputs();
    this.emitValue();

    const next = Math.min(start + digits.length, this.length - 1);
    this.focusAt(next);
    this.checkComplete();
  }

  private checkComplete(): void {
    const value = this.value;
    if (value.length === this.length) {
      this.completed.emit(value);
    }
  }

  private emitValue(): void {
    this.onChange(this.value);
  }

  // Mantém o DOM alinhado com o array (necessário após paste/backspace)
  private syncInputs(): void {
    setTimeout(() => {
      if (!this.boxes) {
        return;
      }
      this.boxes.forEach((box, i) => {
        box.nativeElement.value = this.digits[i] || "";
      });
    });
  }

  private focusAt(index: number): void {
    setTimeout(() => {
      if (!this.boxes) {
        return;
      }
      const target = this.boxes.toArray()[index];
      if (target) {
        target.nativeElement.focus();
        target.nativeElement.select();
      }
    });
  }

  private startCountdown(): void {
    this.clearTimer();
    this.countdown = this.resendSeconds;
    this.intervalId = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearTimer();
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private pad(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
